import { UserSettings } from './user-settings';
import { UserState } from './user-state';
import { UserPermission } from './user-permission';
import { CurrentLicense, getCurrentLicenseFromString, getLicenseString } from '@/core/services/utils/license-state';
import { RoleService } from '@/core/services/role.service';
import { UserForm } from './account';
import { CurrentUserForm, SubuserCreateRequestSettingsDto } from './dtos';
import { CloneUtils } from '@/core/utils/clone-utils';

export interface UserCreateRequestDto {
    settings: UserSettings;
    firstName: string;
    lastName: string;
    email: string;
}

export interface SubuserCreateRequestDto {
    email: string;
    firstName: string;
    lastName: string;
    settings: SubuserCreateRequestSettingsDto;
    permissions: UserPermission[];
    templateId: number;
}

export class User {
    id: number = 0;
    email: string = '';
    firstName: string = '';
    lastName: string = '';
    permissions: UserPermission[];
    state: UserState = UserState.Disabled;
    accountId: number = 0;
    templateId: number = 0;
    settings: UserSettings;
    password?: string;

    constructor() {
        this.settings = new UserSettings();
        this.permissions = [];
    }

    public static fromJson(json: UserForm | null): User {
        if (json != null) {
            const u = new User();
            u.id = json.id;
            u.email = json.email || '';
            u.firstName = json.firstName || '';
            u.lastName = json.lastName || '';
            u.permissions = json.permissions || [];
            u.state = json.state;
            // @ts-expect-error -- TODO fix this when building the SDK!
            u.accountId = json.accountId;
            // @ts-expect-error -- TODO fix this when building the SDK!
            u.templateId = json.templateId;
            u.settings = UserSettings.fromJson(json.settings);
            return u;
        } else {
            return new User();
        }
    }

    public static toUserForm(user: User): UserForm {
        const copy = CloneUtils.cloneData(user) as unknown as UserForm;
        copy.settings = UserSettings.toJson(copy.settings as UserSettings);
        return copy as unknown as UserForm;
    }

    hasPermission(permission: UserPermission): boolean {
        return this.permissions.indexOf(permission) >= 0;
    }

    hasPermissionStr(value: string): boolean {
        const permission = UserPermission[value as keyof typeof UserPermission];
        if (permission) {
            return this.permissions.indexOf(permission) >= 0;
        }
        return false;
    }

    public hasMultiPermission(permissions: UserPermission[]): boolean {
        // checks multiple permissions
        for (const element of permissions) {
            if (!this.hasPermission(element)) {
                return false;
            }
        }
        return true;
    }

    public isAdmin(): boolean {
        return this.hasMultiPermission(RoleService.ADMIN_ROLES);
    }

    public isManager(): boolean {
        return this.hasMultiPermission(RoleService.MANAGER_ROLES);
    }

    public isAdminOrManager(): boolean {
        return this.isAdmin() || this.isManager();
    }

    /**
     * Checks the user's permissions and returns the role name
     * It doesn't check for a 1:1 match of permissions, but rather checks
     * if the user has all the permissions of a role
     * Thus, supersets are allowed but characterized as 'unknown'
     * @returns the role name in string format
     */
    public getRoleString(): string {
        const hasRolePermissions = (rolePermissions: UserPermission[]): boolean =>
            rolePermissions.every((rolePermission) => this.permissions.includes(rolePermission));

        if (hasRolePermissions(RoleService.ADMIN_ROLES)) {
            return 'Admin';
        } else if (hasRolePermissions(RoleService.MANAGER_ROLES)) {
            return 'Manager';
        } else if (hasRolePermissions(RoleService.USER_ROLES)) {
            return 'Regular user';
        } else {
            return 'Unknown role'; // Default case if no roles match
        }
    }

    public hasOneOfPermission(permissions: UserPermission[]): boolean {
        // checks if the user has one of the provided permissions
        for (const element of permissions) {
            if (this.hasPermission(element)) {
                return true;
            }
        }
        return false;
    }

    isNew(): boolean {
        return this.templateId === null;
    }
}

export class CurrentUser extends User {
    license?: CurrentLicense;

    public static override fromJson(json: CurrentUserForm | null): CurrentUser {
        const user = User.fromJson(json);
        const currentUser = Object.assign(new CurrentUser(), user);
        currentUser.license = json ? getCurrentLicenseFromString(json.license) : undefined;
        return currentUser;
    }

    /**
     * Only used by local storage serialization.
     * TODO the local storage serialization and deserialization is actually broken, but it is only used for templateId, which works!
     * @param currentUser
     */
    public static toJsonLocalStorage(currentUser: CurrentUser): string {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO fix type!
        const obj = Object.assign({}, currentUser) as any;
        obj.license = getLicenseString(currentUser.license);
        return JSON.stringify(obj);
    }

    public static fromJsonOptionalLicense(json: CurrentUserForm): CurrentUser {
        const user = User.fromJson(json);
        const currentUser = Object.assign(new CurrentUser(), user);
        if (json.license) {
            currentUser.license = getCurrentLicenseFromString(json.license);
        }
        return currentUser;
    }

    public isPro(): boolean {
        return this.license === CurrentLicense.TRIAL || this.license === CurrentLicense.PRO;
    }

    public getDateFormat(): string {
        return this.settings.dateFormat.toLowerCase().replace('yyyy', 'yy');
    }

    // rename to getHourFormat ?
    public timeFormat12or24(): string {
        return this.settings.timeFormat === 'HH:mm' ? '24' : '12';
    }

    public getFirstDayOfWeek(): number {
        return this.settings.language === 'de' ? 1 : 0;
    }

    public getLocale(): string {
        const lang = this.settings.language ?? 'en';
        return lang === 'de' ? 'de-DE' : 'en-US';
    }
}
